import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getChartOfAccounts,
  createChartOfAccount,
  updateChartOfAccount,
  deleteChartOfAccount,
  updateMultipleChartOfAccounts,
  getCoaRevenueBasisMappings,
} from '../utils/api';
import CoaRevenueMappingTab from './CoaRevenueMappingTab';
import { getLocationsByOrgId } from '../utils/locationApi';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { toast } from 'react-hot-toast';
import { Plus, X } from 'react-feather';
import LoadingScreen from '../components/LoadingScreen';
import { useOptimisticNotifications } from '../hooks/useOptimisticNotifications';
import { InlineSelect, InlineCheckbox, BulkActionBar, SortIcon } from '../components/InlineTable';
import { ConfigPageShell, ScopeSelect, ConfigToolbar, FilterChip, ToolbarButton, ConfigEmptyState } from '../components/ConfigPages';
import { AccountRow, AddAccountRow, COL } from './coa/AccountRow';
import useCoaTaxonomy from './coa/useCoaTaxonomy';
import {
  ACCOUNT_TYPES,
  DEFAULT_VISIBLE_ACCOUNT_TYPES,
  ACCOUNT_GROUPS,
  MATCH_MODES,
  RENDER_CAP,
  BULK_FIELDS,
  PNL_TYPES,
  toOptions,
  optionNameKey,
  normalizeId,
  subDepartmentParentId,
  unionOptions,
  isDeletedRow,
  isUnmapped,
  usaliLabel,
  buildDeptRows,
  withManualFields,
} from './coa/coaShared';

const emptyDraft = { accountNumber: '', accountName: '', accountType: '', accountGroup: '', department: '', subDepartment: '' };
const fmt = (n) => Number(n || 0).toLocaleString();

const ChartOfAccountsPage = () => {
  const { currentOrganization } = useUserSettingsContext();
  const notify = useOptimisticNotifications();
  const orgId = currentOrganization?.id;

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('accounts');

  const [searchTerm, setSearchTerm] = useState('');
  const [matchMode, setMatchMode] = useState('starts');
  const [typeFilter, setTypeFilter] = useState(DEFAULT_VISIBLE_ACCOUNT_TYPES);
  const [status, setStatus] = useState('all'); // all | unmapped | mapped
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'accountNumber', direction: 'ascending' });
  const [showAll, setShowAll] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkField, setBulkField] = useState('department');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const [adding, setAdding] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);

  const scopedLocationId = selectedLocationId === 'all' ? null : selectedLocationId;
  const {
    coaDepartments,
    coaSubDepartments,
    refetchDepartments,
    createAccountGroupOption,
    createDepartmentOption,
    createSubDepartmentOption,
    patchDepartment,
  } = useCoaTaxonomy(orgId, scopedLocationId);

  // ── Data loading ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) { setLocations([]); setSelectedLocationId(null); return; }
    getLocationsByOrgId(orgId)
      .then((data) => {
        setLocations(data || []);
        setSelectedLocationId(data?.[0]?.id ?? null);
      })
      .catch((err) => { console.error('Error fetching locations:', err); setLocations([]); setSelectedLocationId(null); });
  }, [orgId]);

  useEffect(() => {
    if (!orgId) { setLinks([]); setLinksLoading(false); return; }
    setLinksLoading(true);
    getCoaRevenueBasisMappings(orgId)
      .then((rows) => setLinks(rows || []))
      .catch(() => setLinks([]))
      .finally(() => setLinksLoading(false));
  }, [orgId]);

  const refetchAccounts = useCallback(async ({ silent = false } = {}) => {
    if (!orgId || !selectedLocationId) { setAccounts([]); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const data = await getChartOfAccounts(orgId, scopedLocationId || undefined, { includeDeleted: showDeleted });
      setAccounts(data || []);
    } catch (err) {
      console.error('Error fetching chart of accounts:', err);
      toast.error('Failed to load chart of accounts. Refresh to try again.');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, selectedLocationId, scopedLocationId, showDeleted]);

  useEffect(() => { refetchAccounts(); }, [refetchAccounts]);

  useEffect(() => { setSelectedIds(new Set()); setShowAll(false); }, [selectedLocationId, typeFilter, searchTerm, status, showDeleted]);

  // ── Option lists ───────────────────────────────────────────────────────
  const accountTypesForUi = useMemo(() => {
    // Dedupe: without this, an off-catalog type (e.g. "Statistical") produces one
    // entry per account, rendering duplicate chips that share a key + filter state.
    const extras = [...new Set(
      accounts.map((a) => a.accountType).filter(Boolean).filter((t) => !ACCOUNT_TYPES.includes(t))
    )].sort((a, b) => a.localeCompare(b));
    return [...ACCOUNT_TYPES, ...extras];
  }, [accounts]);

  const accountTypeOptions = useMemo(() => toOptions(accountTypesForUi), [accountTypesForUi]);

  const accountGroupOptions = useMemo(() => unionOptions(
    [], [...accounts.map((a) => a.accountGroup), draft.accountGroup], ACCOUNT_GROUPS
  ), [accounts, draft.accountGroup]);

  const deptOptions = useMemo(() => unionOptions(
    coaDepartments.map((d) => d.name), [...accounts.map((a) => a.department), draft.department], []
  ), [coaDepartments, accounts, draft.department]);

  const deptByName = useMemo(() => {
    const m = new Map();
    coaDepartments.forEach((d) => m.set(optionNameKey(d.name), d));
    return m;
  }, [coaDepartments]);

  const allSubDeptOptions = useMemo(() => unionOptions(
    coaSubDepartments.map((s) => s.name), [...accounts.map((a) => a.subDepartment), draft.subDepartment], []
  ), [coaSubDepartments, accounts, draft.subDepartment]);

  /** Scope sub-departments to the row's department when the hierarchy is known. */
  const subDeptOptionsFor = useCallback((account) => {
    const deptId = normalizeId(deptByName.get(optionNameKey(account.department))?.id);
    if (deptId == null) return allSubDeptOptions;
    const scoped = coaSubDepartments.filter((s) => subDepartmentParentId(s) === deptId).map((s) => s.name);
    if (scoped.length === 0) return allSubDeptOptions;
    return unionOptions(scoped, [account.subDepartment], []);
  }, [deptByName, coaSubDepartments, allSubDeptOptions]);

  // P&L rows (department → section → links) — shared by the flow rail and
  // the P&L Mapping tab; also powers the "lands in" line under Department.
  const pnlDeptRows = useMemo(() => buildDeptRows(accounts, coaDepartments, links), [accounts, coaDepartments, links]);
  const sectionByDeptKey = useMemo(() => {
    const m = new Map();
    pnlDeptRows.forEach((d) => {
      const label = d.section ? usaliLabel(d.section) : `${usaliLabel(d.inferred)} (auto)`;
      m.set(optionNameKey(d.name), label);
      // Accounts attach by FK, so a renamed department's rows may still carry the old string.
      d.accounts.forEach((a) => { if (a.department) m.set(optionNameKey(a.department), label); });
    });
    return m;
  }, [pnlDeptRows]);
  const landsInFor = useCallback((account) => {
    if (isUnmapped(account) || !PNL_TYPES.has(account.accountType)) return null;
    const section = sectionByDeptKey.get(optionNameKey(account.department));
    return section ? `${section} · ${account.accountType}` : null;
  }, [sectionByDeptKey]);

  // ── Derived rows ───────────────────────────────────────────────────────
  const liveAccounts = useMemo(() => accounts.filter((a) => !isDeletedRow(a)), [accounts]);
  const deletedCount = accounts.length - liveAccounts.length;

  const typeCounts = useMemo(() => {
    const counts = Object.fromEntries(accountTypesForUi.map((t) => [t, 0]));
    liveAccounts.forEach((a) => {
      if (!a.accountType) return;
      if (counts[a.accountType] === undefined) counts[a.accountType] = 0;
      counts[a.accountType] += 1;
    });
    return counts;
  }, [liveAccounts, accountTypesForUi]);

  const typeFiltered = useMemo(() => (
    typeFilter.length === 0 ? accounts : accounts.filter((a) => typeFilter.includes(a.accountType))
  ), [accounts, typeFilter]);

  const searched = useMemo(() => {
    if (!searchTerm) return typeFiltered;
    const term = searchTerm.toLowerCase();
    const match = (value) => {
      if (!value) return false;
      const v = String(value).toLowerCase();
      if (matchMode === 'starts') return v.startsWith(term);
      if (matchMode === 'ends') return v.endsWith(term);
      return v.includes(term);
    };
    return typeFiltered.filter((a) =>
      match(a.accountNumber) || match(a.accountName) ||
      (matchMode === 'contains' && (match(a.accountGroup) || match(a.department) || match(a.subDepartment)))
    );
  }, [typeFiltered, searchTerm, matchMode]);

  // Status counts follow the type + search filters; deleted rows only show under All.
  const statusCounts = useMemo(() => {
    let unmapped = 0; let mapped = 0;
    searched.forEach((a) => { if (isDeletedRow(a)) return; if (isUnmapped(a)) unmapped += 1; else mapped += 1; });
    return { all: searched.length, unmapped, mapped };
  }, [searched]);

  const filteredAndSorted = useMemo(() => {
    let rows = searched;
    if (status === 'unmapped') rows = rows.filter((a) => !isDeletedRow(a) && isUnmapped(a));
    else if (status === 'mapped') rows = rows.filter((a) => !isDeletedRow(a) && !isUnmapped(a));
    const { key, direction } = sortConfig;
    const dir = direction === 'ascending' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[key] ?? ''; const bv = b[key] ?? '';
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [searched, status, sortConfig]);

  const visibleRows = showAll ? filteredAndSorted : filteredAndSorted.slice(0, RENDER_CAP);
  const hiddenCount = filteredAndSorted.length - visibleRows.length;

  // Flow rail + coverage: the type chips scope the chart, so the rail follows them.
  const liveTyped = useMemo(() => typeFiltered.filter((a) => !isDeletedRow(a)), [typeFiltered]);
  const needsDeptCount = useMemo(() => liveTyped.filter(isUnmapped).length, [liveTyped]);
  const withDeptCount = liveTyped.length - needsDeptCount;
  const sectionsInUse = useMemo(() => new Set(pnlDeptRows.map((d) => d.section || d.inferred)).size, [pnlDeptRows]);

  // ── Editing ────────────────────────────────────────────────────────────
  const handleField = useCallback(async (accountId, field, value) => {
    const account = accounts.find((a) => a.id === accountId);
    if (!account || account[field] === value) return;
    const originalAccount = { ...account };
    // Changing the department invalidates its sub-department; clear both in
    // one PUT so the row never carries a sub-department of another department.
    let payload; let localPatch;
    if (field === 'department') {
      payload = { department: value, subDepartment: '' };
      localPatch = { department: value, subDepartment: '' };
    } else if (field === 'subDepartment' && account.department) {
      payload = { department: account.department, subDepartment: value };
      localPatch = { subDepartment: value };
    } else {
      payload = { [field]: value };
      localPatch = { [field]: value };
    }
    // The server locks edited fields against the accounting sync; mirror it
    // so the lock shows before the response lands.
    setAccounts((prev) => prev.map((a) => (a.id === accountId ? withManualFields({ ...a, ...localPatch }, Object.keys(payload)) : a)));
    try {
      await notify.update(
        `Account ${account.accountNumber} ${field}`,
        updateChartOfAccount(accountId, orgId, payload),
        {
          onSuccess: (updated) => setAccounts((prev) => prev.map((a) => (a.id === accountId ? { ...a, ...updated } : a))),
          revertAction: () => setAccounts((prev) => prev.map((a) => (a.id === accountId ? originalAccount : a))),
        }
      );
    } catch (err) {
      console.error(`Error updating ${field} for account ${accountId}:`, err);
    }
  }, [accounts, orgId, notify]);

  const handleDelete = useCallback(async (account) => {
    const original = accounts;
    // Optimistic: with Show deleted on the row stays (struck through); otherwise it leaves the list.
    setAccounts((prev) => (showDeleted
      ? prev.map((a) => (a.id === account.id ? { ...a, isDeleted: true } : a))
      : prev.filter((a) => a.id !== account.id)));
    setSelectedIds((prev) => { const n = new Set(prev); n.delete(account.id); return n; });
    try {
      await deleteChartOfAccount(account.id, orgId);
      toast.success(`Deleted ${account.accountNumber}. Restore it from Show deleted.`);
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error(`Failed to delete ${account.accountNumber}. Try again.`);
      setAccounts(original);
    }
  }, [accounts, orgId, showDeleted]);

  const handleRestore = useCallback(async (account) => {
    const original = accounts;
    setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, isDeleted: false } : a)));
    try {
      const updated = await updateChartOfAccount(account.id, orgId, { isDeleted: false });
      if (updated) setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, ...updated, isDeleted: false } : a)));
      toast.success(`Restored ${account.accountNumber}.`);
    } catch (err) {
      console.error('Error restoring account:', err);
      toast.error(`Failed to restore ${account.accountNumber}. Try again.`);
      setAccounts(original);
    }
  }, [accounts, orgId]);

  // Hand the row back to the accounting sync: clears manualFields so the next
  // run may overwrite every field again.
  const handleReleaseLock = useCallback(async (account) => {
    const original = accounts;
    setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, manualFields: null } : a)));
    try {
      const updated = await updateChartOfAccount(account.id, orgId, { manualFields: [] });
      if (updated) setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, ...updated, manualFields: updated.manualFields ?? null } : a)));
      toast.success(`Released ${account.accountNumber}. The accounting sync manages it again.`);
    } catch (err) {
      console.error('Error releasing account lock:', err);
      toast.error(`Failed to release ${account.accountNumber}. Try again.`);
      setAccounts(original);
    }
  }, [accounts, orgId]);

  // ── Selection + bulk ───────────────────────────────────────────────────
  const visibleIds = useMemo(() => filteredAndSorted.filter((a) => !isDeletedRow(a)).map((a) => a.id), [filteredAndSorted]);
  const selectedVisibleCount = useMemo(() => visibleIds.filter((id) => selectedIds.has(id)).length, [visibleIds, selectedIds]);
  const allSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;

  const toggleOne = useCallback((id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(visibleIds));

  const bulkOptions = useMemo(() => {
    if (bulkField === 'accountType') return accountTypeOptions;
    if (bulkField === 'accountGroup') return accountGroupOptions;
    if (bulkField === 'department') return deptOptions;
    return allSubDeptOptions;
  }, [bulkField, accountTypeOptions, accountGroupOptions, deptOptions, allSubDeptOptions]);

  const handleBulkApply = async (value) => {
    if (value == null || selectedVisibleCount === 0) return;
    const ids = visibleIds.filter((id) => selectedIds.has(id));
    setIsBulkUpdating(true);
    const original = accounts.map((a) => ({ ...a }));
    // The backend clears sub-department when the department changes and locks
    // the edited field against the accounting sync — mirror both locally.
    const patch = bulkField === 'department' ? { department: value, subDepartment: '' } : { [bulkField]: value };
    setAccounts((prev) => prev.map((a) => (ids.includes(a.id) ? withManualFields({ ...a, ...patch }, [bulkField]) : a)));
    try {
      await notify.success(
        `Updated ${ids.length} account${ids.length === 1 ? '' : 's'}.`,
        updateMultipleChartOfAccounts(orgId, ids, bulkField, value),
        'Bulk update failed. Nothing was changed — try again.',
        {
          onSuccess: () => setSelectedIds(new Set()),
          revertAction: () => setAccounts(original),
        }
      );
    } catch (err) {
      console.error('Error during bulk update:', err);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // ── Add account ────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!draft.accountNumber || !draft.accountName || !draft.accountType || !draft.accountGroup) {
      toast.error('Account number, name, type, and group are required.');
      return;
    }
    setIsCreating(true);
    // "All locations" = an org-wide account (locationId null), never the string 'all'.
    // source: 'user' marks this as a hand entry: it may restore a deleted row
    // with the same number, and the fields typed here are locked against the
    // accounting-system sync overwriting them.
    const accountData = { ...draft, locationId: scopedLocationId ?? null, source: 'user' };
    const optimistic = { id: `temp-${Date.now()}`, ...accountData, isOptimistic: true };
    if (draft.accountType && !typeFilter.includes(draft.accountType)) {
      setTypeFilter((prev) => (prev.includes(draft.accountType) ? prev : [...prev, draft.accountType]));
    }
    setAccounts((prev) => [optimistic, ...prev]);
    try {
      await notify.create(
        `Account ${draft.accountNumber}`,
        createChartOfAccount(orgId, accountData),
        {
          onSuccess: (created) => {
            // An upsert may have updated an existing row (same number) — replace
            // the optimistic row and drop the stale duplicate.
            setAccounts((prev) => prev
              .filter((a) => a.id === optimistic.id || a.id !== created?.id)
              .map((a) => (a.id === optimistic.id ? created : a)));
            if (created?._upsertAction === 'updated') {
              toast(`Updated existing account ${created.accountNumber || draft.accountNumber}`);
            } else if (created?._upsertAction === 'restored') {
              toast(`Restored deleted account ${created.accountNumber || draft.accountNumber}`);
            }
            setDraft(emptyDraft);
            setAdding(false);
          },
          revertAction: () => setAccounts((prev) => prev.filter((a) => a.id !== optimistic.id)),
        }
      );
    } catch (err) {
      console.error('Error creating account:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const requestSort = (key) => setSortConfig((prev) => ({
    key,
    direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
  }));

  const hasSegments = useMemo(() => accounts.some((a) =>
    (a.accountPrefix != null && a.accountPrefix !== '') || (a.accountSuffix != null && a.accountSuffix !== '')
  ), [accounts]);

  const locationOptions = useMemo(
    () => [{ value: 'all', label: 'All locations' }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations]
  );
  const locationName = scopedLocationId ? (locations.find((l) => l.id === scopedLocationId)?.name || null) : null;

  // ── Render ─────────────────────────────────────────────────────────────
  if (!currentOrganization) {
    return <div className="p-4">Please select an organization first.</div>;
  }
  if (loading && accounts.length === 0) {
    return <LoadingScreen message="Loading Chart of Accounts" />;
  }

  const sortTh = (key, label, style) => (
    <th className="it-sort" style={style} onClick={() => requestSort(key)}>
      {label} <SortIcon columnKey={key} sortKey={sortConfig.key} direction={sortConfig.direction} />
    </th>
  );

  const pnlWithSection = pnlDeptRows.filter((d) => d.section).length;
  const pnlLinked = pnlDeptRows.filter((d) => d.links.length > 0).length;

  const flow = activeTab === 'accounts'
    ? {
      stations: [
        {
          label: typeFilter.length && liveTyped.length !== liveAccounts.length ? 'Accounts (filtered)' : 'Accounts',
          value: fmt(liveTyped.length),
          sub: typeFilter.length && liveTyped.length !== liveAccounts.length
            ? `of ${fmt(liveAccounts.length)} · ${typeFilter.filter((t) => typeCounts[t]).join(' · ')}`
            : 'All types',
        },
        { label: 'Department', value: `${fmt(withDeptCount)} assigned`, tone: withDeptCount > 0 ? 'ok' : 'neutral' },
        ...(needsDeptCount > 0 ? [{ label: 'Needs department', value: fmt(needsDeptCount), tone: 'warn', sub: 'Excluded from dashboards' }] : []),
        { label: 'Lands in', value: 'P&L · USALI sections', mono: false, sub: `${sectionsInUse} section${sectionsInUse === 1 ? '' : 's'} in use` },
      ],
      coverage: { mapped: withDeptCount, total: liveTyped.length, label: 'with a department' },
      onUnmappedClick: () => setStatus('unmapped'),
    }
    : {
      stations: [
        { label: 'Departments', value: fmt(pnlDeptRows.length), sub: 'With P&L accounts' },
        { label: 'USALI section', value: `${fmt(pnlWithSection)} set`, tone: pnlWithSection > 0 ? 'ok' : 'neutral', sub: `${fmt(pnlDeptRows.length - pnlWithSection)} inferred` },
        { label: 'Measured against revenue', value: `${fmt(pnlLinked)} linked`, sub: 'Others pair by name / total revenue' },
        { label: 'Lands in', value: 'P&L · Dept & GOP views', mono: false, sub: '% Rev, departmental profit, GOP' },
      ],
      coverage: { mapped: pnlWithSection, total: pnlDeptRows.length, label: 'with a USALI section' },
    };

  return (
    <ConfigPageShell
      title="Chart of accounts"
      subtitle="Every GL account, its department and where it lands on the P&L."
      tabs={[{ key: 'accounts', label: 'Accounts', count: fmt(liveAccounts.length) }, { key: 'pnl', label: 'P&L mapping' }]}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      scope={locations.length > 1 ? (
        <ScopeSelect>
          <InlineSelect
            value={selectedLocationId}
            options={locationOptions}
            onChange={(v) => v != null && setSelectedLocationId(v)}
            placeholder="Location…"
            rowActive
            clearable={false}
            minWidth={220}
            ariaLabel="Location"
          />
        </ScopeSelect>
      ) : null}
      flow={flow}
    >
      {activeTab === 'pnl' && (
        <CoaRevenueMappingTab
          organizationId={orgId}
          locationId={scopedLocationId}
          locationName={locationName}
          accounts={accounts}
          departments={coaDepartments}
          deptRows={pnlDeptRows}
          links={links}
          linksLoading={linksLoading}
          onLinksChange={setLinks}
          onPatchDepartment={patchDepartment}
          refetchDepartments={refetchDepartments}
        />
      )}

      {activeTab === 'accounts' && (
        <div className="it-frame">
          <ConfigToolbar
            search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search number or name…' }}
            status={{ value: status, counts: statusCounts, onChange: setStatus }}
            chips={(
              <>
                <div className="cp-seg" role="group" aria-label="Search match mode" title="How the search term matches">
                  {MATCH_MODES.map((m) => (
                    <button key={m.key} type="button" className={matchMode === m.key ? 'cp-active' : ''} onClick={() => setMatchMode(m.key)} aria-pressed={matchMode === m.key}>
                      {m.label}
                    </button>
                  ))}
                </div>
                {accountTypesForUi.map((t) => {
                  const active = typeFilter.includes(t);
                  return (
                    <FilterChip key={t} active={active} count={typeCounts[t]} onClick={() => setTypeFilter((prev) => (active ? prev.filter((x) => x !== t) : [...prev, t]))}>
                      {t}
                    </FilterChip>
                  );
                })}
              </>
            )}
            showDeleted={{ value: showDeleted, onChange: setShowDeleted, count: showDeleted ? deletedCount : undefined }}
            meta={`${fmt(filteredAndSorted.length)} of ${fmt(liveAccounts.length)} accounts`}
            actions={(
              <ToolbarButton
                variant={adding ? 'default' : 'primary'}
                icon={adding ? X : Plus}
                onClick={() => { setAdding((a) => !a); setDraft(emptyDraft); }}
              >
                {adding ? 'Cancel' : 'Add account'}
              </ToolbarButton>
            )}
          />

          {selectedVisibleCount > 0 && (
            <BulkActionBar
              count={selectedVisibleCount}
              noun={`account${selectedVisibleCount !== 1 ? 's' : ''} selected`}
              actionLabel={`Set ${BULK_FIELDS.find((f) => f.key === bulkField)?.label.toLowerCase()}:`}
              options={bulkOptions}
              placeholder="Choose…"
              applying={isBulkUpdating}
              onApply={handleBulkApply}
              onClear={() => setSelectedIds(new Set())}
            >
              <div style={{ display: 'flex', gap: 4, flexShrink: 0, order: -1 }}>
                {BULK_FIELDS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setBulkField(f.key)}
                    aria-pressed={bulkField === f.key}
                    style={{
                      padding: '4px 9px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                      fontFamily: 'var(--font-sans)', transition: 'all .1s',
                      border: '1px solid rgba(255,255,255,.14)',
                      background: bulkField === f.key ? 'rgba(255,255,255,.16)' : 'transparent',
                      color: bulkField === f.key ? 'var(--lodge-onyx-50)' : 'rgba(255,255,255,.45)',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </BulkActionBar>
          )}

          <div className="it-scroll">
            <table className="it-tbl" style={{ minWidth: 1260 }}>
              <thead>
                <tr>
                  <th className="cp-cell" style={COL.check}>
                    <InlineCheckbox
                      checked={allSelected}
                      indeterminate={selectedVisibleCount > 0 && !allSelected}
                      onChange={toggleAll}
                      disabled={visibleIds.length === 0}
                      title={`Select all ${visibleIds.length} visible accounts`}
                    />
                  </th>
                  {sortTh('accountNumber', 'Account #', COL.number(hasSegments))}
                  {sortTh('accountName', 'Account name', COL.name)}
                  {sortTh('accountType', 'Type', COL.type)}
                  {sortTh('accountGroup', 'Group', COL.group)}
                  {sortTh('department', 'Department', COL.dept)}
                  {sortTh('subDepartment', 'Sub-department', COL.sub)}
                  <th style={COL.actions} />
                  <th style={COL.status}>Status</th>
                </tr>
              </thead>
              <tbody>
                {adding && (
                  <AddAccountRow
                    draft={draft}
                    setDraft={setDraft}
                    hasSegments={hasSegments}
                    accountTypeOptions={accountTypeOptions}
                    accountGroupOptions={accountGroupOptions}
                    deptOptions={deptOptions}
                    subDeptOptionsFor={subDeptOptionsFor}
                    onCreateAccountGroup={createAccountGroupOption}
                    onCreateDepartment={createDepartmentOption}
                    onCreateSubDepartment={createSubDepartmentOption}
                    onSave={handleCreate}
                    onCancel={() => { setAdding(false); setDraft(emptyDraft); }}
                    saving={isCreating}
                  />
                )}

                {visibleRows.map((account) => (
                  <AccountRow
                    key={account.id}
                    account={account}
                    selected={selectedIds.has(account.id)}
                    hasSegments={hasSegments}
                    landsIn={landsInFor(account)}
                    accountTypeOptions={accountTypeOptions}
                    accountGroupOptions={accountGroupOptions}
                    deptOptions={deptOptions}
                    subDeptOptionsFor={subDeptOptionsFor}
                    onCreateAccountGroup={createAccountGroupOption}
                    onCreateDepartment={createDepartmentOption}
                    onCreateSubDepartment={createSubDepartmentOption}
                    onToggle={toggleOne}
                    onField={handleField}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                    onReleaseLock={handleReleaseLock}
                  />
                ))}

                {visibleRows.length === 0 && !adding && (
                  <tr>
                    <td colSpan={9}>
                      {accounts.length === 0 ? (
                        <ConfigEmptyState
                          title="No accounts here yet"
                          body={scopedLocationId ? 'This location has no chart of accounts. Add one by hand or import a chart.' : 'This organization has no chart of accounts yet.'}
                          action={<ToolbarButton variant="primary" icon={Plus} onClick={() => { setAdding(true); setDraft(emptyDraft); }}>Add account</ToolbarButton>}
                        />
                      ) : (
                        <ConfigEmptyState
                          title="No accounts match"
                          body="Try a different search, match mode, or type filter."
                          action={<ToolbarButton variant="ghost" onClick={() => { setSearchTerm(''); setStatus('all'); setTypeFilter(DEFAULT_VISIBLE_ACCOUNT_TYPES); }}>Clear filters</ToolbarButton>}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {hiddenCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0', borderTop: '1px solid var(--lodge-onyx-100)' }}>
                <ToolbarButton onClick={() => setShowAll(true)}>
                  Show all {fmt(filteredAndSorted.length)} accounts
                  <span className="cp-meta">+{fmt(hiddenCount)}</span>
                </ToolbarButton>
              </div>
            )}
          </div>
        </div>
      )}
    </ConfigPageShell>
  );
};

export default ChartOfAccountsPage;
